import { LevelCatalog, isNewGear } from "@highmarch/app";
import { BalanceData, PlayerProfile, UpgradeJob, UpgradeTarget } from "@highmarch/domain";
import { audio } from "../../audio/audio";
import { LootFlight, takeLootFlight } from "../loot";

import { toast } from "../toast";
import { UpgradeSheet } from "./upgradeSheet";
import { TowersTab } from "./towersTab";
import { HubTab, SheetGuard, tabForSheet } from "./sheetGuard";
import { BuilderPick, builderPick, pickTarget } from "./builderPick";
import { DONE_BADGE_SECONDS } from "./badges";
import { UpgradeNudge, upgradeNudge } from "./upgradeNudge";
import { ownedTowerIds, unseenTowers } from "./news";
import { Readiness, ReadinessAction, readinessFor } from "./readiness";
import { OrdersCtx, mountOrders } from "./ordersSheet";
import { showCheckin } from "./checkinCard";
import { showMore } from "./moreSheet";
import { CoreMoreRow, HubFeatures } from "./hubFeatures";
import { EVENTS_HEAD, eventsPageIntro } from "./eventsPage";
import { researchFacts } from "./researchLink";
import { MORE_ORDER } from "../../features/registry";
import { showCommander } from "./commanderSheet";
import { HUB_UNLOCK_LEVEL, playParkedUnlockCards } from "../unlockCards";
import { guide } from "../guide/guide";
import { hubFacts, sheetName } from "./hubGuide";
import { SceneLink } from "./sceneApi";
import { eventsBadgeHtml, hubHtml, menuDotHtml, shopBadgeHtml, towersBadgeHtml } from "./hubMarkup";
import { HubGuideLink } from "./hubGuideLink";
import { HubSceneLink } from "./hubSceneLink";
import { appHeight } from "../appSize";
import { showPrize } from "./prizeFocus";

export interface HubHandlers {
  onStart: (level: number) => void;
  onChange: () => void;
  onCampaign: () => void;
  /** Opens the Armoury (the Gear tab). */
  onInventory: () => void;
  onSettings: () => void;
  onDebug: (() => void) | null;
  /** What the save records as seen (news.ts): drives the Towers tab's NEW badge. */
  seen: HubSeen;
  /** Every hub tick (1 s): main.ts re-tries what waits for the guide to go quiet (the install card, order toasts). */
  onTick?: () => void;
}

export interface HubSeen {
  towers(): readonly string[] | undefined;
  markTowers(ids: readonly string[]): void;
  /** The Gear tab's NEW: the highest gear id seen in the Armoury (SaveRecord.gearSeenUpTo). */
  gearSeenUpTo?(): number | undefined;
}

/** The Shop, the Armoury and Events stay dimmed and locked until the first level is cleared (progressive disclosure). */
const UNLOCK_AFTER_LEVEL = HUB_UNLOCK_LEVEL;
/** What a locked tab's toast calls it. */
const TAB_NAMES: Readonly<Record<HubTab, string>> = { shop: "the Shop", towers: "Towers", battle: "Battle", gear: "the Armoury", events: "Events" };
/** A sheet this tall (fraction of the viewport) hides most of the scene: the hub scene stops rendering under it. */
const PAUSE_SHEET_FRACTION = 0.6;

/**
 * The lobby hub (hub redesign, /tmp/hubconcepts DECISION.md; M35 Option 1): top bar (commander chip, the four
 * currency pills — each opens the Shop on its own page — and the ⚙ menu), the 3D scene band with the builder chip on
 * the fortress, the battle card (chapter row → map, the next level, the readiness strip), PLAY, and a five-tab bar —
 * Shop · Towers · BATTLE · Armoury · Events — with one entry point per destination. Tab pages are sheets that end above
 * the tab bar, so it stays live under them. Features add pills, badge counts, menu and Events rows, widgets and tab
 * pages through the feature registry (hubFeatures.ts), not by editing this file.
 * The markup is hubMarkup.ts, the guide hook-up hubGuideLink.ts, the scene band hubSceneLink.ts.
 */
export class HubView {
  readonly root: HTMLDivElement;
  private readonly profile: PlayerProfile;
  private readonly upgrade: UpgradeSheet;
  private readonly towers: TowersTab;
  private readonly guard: SheetGuard;
  private readonly scene: SceneLink;
  private readonly sceneBand: HubSceneLink;
  private readonly guideLink: HubGuideLink;
  private readonly features: HubFeatures;
  /** The lit tab: the page whose sheet is up, else Battle (sheetGuard.ts `tabForSheet`). */
  private tab: HubTab = "battle";
  private timer: ReturnType<typeof setInterval> | null = null;
  /** Chest loot still "in the air": the pills show this much less until the flight lands (loot.ts). */
  private inFlight: LootFlight | null = null;
  /** Until when (clock seconds) the Towers tab shows ✓: a job finished while the hub was open. */
  private doneUntil = 0;
  private readiness!: Readiness;
  private pick!: BuilderPick;
  private readonly onResize = () => this.sceneBand.layout(this.pick, this.features);
  /** Chest loot flies home (ui/rewardFly, after the cloud wipe opens) within this long of the hub's coming up. */
  static readonly LootFlightDelayMs = 650;
  /** The day's check-in card waits this long once the hub is quiet (DESIGN §4.2: after the captain's first session). */
  static readonly CheckinDelayMs = 450;
  /** The loot flight holds the guide this long after it starts (DESIGN §4.3 "loading"). */
  static readonly LootBusyMs = 1600;
  /** performance.now() until which the loot flight counts as busy. */
  private lootBusyUntil = 0;

  constructor(private readonly parent: HTMLElement, private readonly catalog: LevelCatalog, private readonly now: () => number, private readonly h: HubHandlers,
              scene: object | null = null, private readonly version = "") {
    this.profile = catalog.profile;
    this.scene = new SceneLink(scene);
    this.root = document.createElement("div");
    this.root.className = "hub";
    this.sceneBand = new HubSceneLink(this.scene, this.root, catalog, this.profile);
    this.guideLink = new HubGuideLink({ parent, root: this.root, catalog, play: (level) => h.onStart(level), lootBusy: () => performance.now() < this.lootBusyUntil,
      facts: (sheet) => hubFacts({ B: this.B, profile: this.profile, highestCleared: catalog.highestLevelCleared, nextLevel: this.nextLevel, readiness: this.readiness, pick: this.pick, newTowerIds: this.newTowers(), ordersUnlocked: catalog.orders.unlocked(catalog.highestLevelCleared), sheet }) });
    parent.append(this.root);
    parent.classList.add("hubtabs");                         // hub.css: sheets end above the live tab bar
    const ctx = { balance: this.B, profile: this.profile, now, onChange: () => { h.onChange(); this.render(); }, freeFinish: catalog.deals, onJobsDone: (done: UpgradeJob[]) => this.jobsDone(done), catalog };
    this.upgrade = new UpgradeSheet(parent, ctx);
    this.towers = new TowersTab(parent, catalog, now, (i) => this.upgrade.open(i), (id) => this.upgrade.openPet(id));
    this.inFlight = takeLootFlight();
    if (this.inFlight) this.lootBusyUntil = performance.now() + HubView.LootFlightDelayMs + HubView.LootBusyMs;
    this.guard = new SheetGuard(parent, (sheet) => this.onSheet(sheet));
    this.features = new HubFeatures({
      parent, changed: () => { h.onChange(); this.render(); }, openTab: (tab) => this.onTab(tab), play: (level) => h.onStart(level),
      anchor: (target) => this.scene.anchor(target),
      openUpgrade: (i) => this.upgrade.open(i),
      openPet: (id) => this.upgrade.openPet(id),
      openArmoury: () => h.onInventory(),
    });
    // A save from before the NEW badge: what the player owns now is not news.
    if (h.seen.towers() === undefined) h.seen.markTowers(ownedTowerIds(this.B, catalog.ownedTowers()));
    this.render();
    this.unfollow = this.sceneBand.follow(() => this.pick, this.features);
    // Unlock cards the player never saw (left the end screen without COLLECT) play over the hub; the guide waits for them.
    playParkedUnlockCards(parent, () => this.guideLink.update());
    this.maybeShowCheckin();
    this.timer = setInterval(() => this.tick(), 1000);
    addEventListener("resize", this.onResize);
  }

  /**
   * The check-in card comes up by itself on the first hub visit of a day (once), when Orders are unlocked, once the
   * captain is quiet (his first session is over) and nothing else is on screen; re-checked on every tick until then.
   */
  private maybeShowCheckin(): void {
    const ci = this.catalog.checkin, now = this.now();
    if (!this.catalog.orders.unlocked(this.catalog.highestLevelCleared) || !ci.shouldShow(now)) return;
    if (guide()?.quiet() === false || this.guideLink.busy() !== null) return;
    ci.markShown(now);
    this.h.onChange();
    window.setTimeout(() => { if (this.root.isConnected) showCheckin(this.ordersCtx()); }, HubView.CheckinDelayMs);
  }

  private ordersCtx(): OrdersCtx {
    return { catalog: this.catalog, now: this.now, parent: this.parent, onChange: () => { this.h.onChange(); this.render(); }, onCheckin: () => showCheckin(this.ordersCtx()) };
  }

  private get B(): BalanceData { return this.catalog.balance; }
  private get nextLevel(): number { return Math.min(this.catalog.levelCount, this.catalog.highestLevelCleared + 1); }
  private get unlocked(): boolean { return this.catalog.highestLevelCleared >= UNLOCK_AFTER_LEVEL; }
  /** Gear won since the Armoury last opened (its tab's NEW). */
  private newGear(): number { const upTo = this.h.seen.gearSeenUpTo?.(); return this.profile.gear.filter((g) => isNewGear(g, { gearSeenUpTo: upTo })).length; }
  /** Owned towers not yet seen on the Towers page. */
  private newTowers(): string[] { return unseenTowers(this.B, this.catalog.ownedTowers(), this.h.seen.towers()); }
  private towersBadgeHtml(): string { return towersBadgeHtml(this.features, this.pick, this.now() < this.doneUntil, this.newTowers().length); }
  /** Research nodes the player can buy now (the Shop's own red count; none until the research module reports). */
  private researchDots(): number { return researchFacts(this.profile)?.available ? 1 : 0; }

  rerender(): void { this.render(); }

  /** PLAY: the next level (the campaign map once every level is cleared). The Armoury's Battle takes this path too. */
  playNext(): void {
    this.guideLink.signal("playPressed");
    if (this.catalog.highestLevelCleared >= this.catalog.levelCount) this.h.onCampaign(); else this.h.onStart(this.nextLevel);
  }

  /** Opens a tab's page as a tap on it would (the Armoury's "Towers ›" opens Towers). */
  openTab(tab: HubTab): void { this.onTab(tab); }

  private render(): void {
    // M44: a render after dispose (a sheet's or a widget's late `changed`) mounted a fresh set of widgets into the
    // detached root, with loops and listeners nobody would dispose
    if (this.disposed) return;
    const p = this.profile, next = this.nextLevel;
    // The owned roster as the Armoury scopes Equip best, so the Gear badge, EQUIP n and its count agree.
    this.readiness = readinessFor(p, next, { roster: this.catalog.ownedTowers() });
    this.pick = builderPick(this.B, p, this.catalog.ownedTowers(), this.now());
    this.root.innerHTML = hubHtml({
      catalog: this.catalog, profile: p, features: this.features, next, readiness: this.readiness, pick: this.pick, inFlight: this.inFlight,
      locked: !this.unlocked, tab: this.tab, towersDone: this.now() < this.doneUntil, newTowers: this.newTowers().length, now: this.now(),
      newGear: this.newGear(), shopDots: this.researchDots(),
    });
    const q = <T extends HTMLElement>(sel: string) => this.root.querySelector<T>(sel);
    q<HTMLButtonElement>(".commander")!.onclick = () => this.openCommander();
    // the top bar's buttons (the Store's gems button, VIP) are the features' own pills
    this.features.bindPills(this.root);
    q<HTMLButtonElement>(".topbar .menubtn")!.onclick = () => this.openMenu();
    q<HTMLButtonElement>(".battlecard .chap")!.onclick = () => this.h.onCampaign();
    q<HTMLButtonElement>(".battlecard .rbody")?.addEventListener("click", () => this.openCommander());
    const prize = q<HTMLButtonElement>(".battlecard .prize");
    if (prize) prize.onclick = () => showPrize(this.parent, this.B, Number(prize.dataset.level), this.catalog.isCleared(Number(prize.dataset.level)));
    q<HTMLButtonElement>(".bchip")!.onclick = () => this.openPick();
    const play = q<HTMLButtonElement>(".play")!;
    play.onclick = () => this.playNext();
    play.insertAdjacentHTML("beforeend", `<span class="shine"></span>`);   // F8 idle shine sweep (juice.css)
    for (const b of this.root.querySelectorAll<HTMLButtonElement>(".tabbar .tab")) b.onclick = () => this.onTab(b.dataset.tab as HubTab);
    this.features.mountWidgets(q<HTMLElement>(".chips")!);
    this.sceneBand.sync();
    this.sceneBand.layout(this.pick, this.features);
    this.guideLink.update();
  }

  /** The Shop tab on `page` (the lit Shop swaps page rather than closing). */
  private openShop(page: "gold" | "gems" | "marks" | "essence"): void {
    if (!this.unlocked) { toast(this.parent, `Clear level ${UNLOCK_AFTER_LEVEL} to unlock the Shop`); return; }
    guide()?.signal("tabOpened");
    this.features.openShop(page);
  }

  /**
   * One tap per page: the lit tab closes the page back to the lobby, any other tab swaps to its page. BATTLE closes
   * whatever is up and, on the lobby itself, plays the next level as PLAY does (M35: the bar's centre names it).
   */
  private onTab(tab: HubTab): void {
    guide()?.signal("tabOpened");
    if (tab === "battle") {
      if (this.guard.hasSheet()) { this.guard.closeTop(); return; }
      this.playNext();
      return;
    }
    if (tab === this.tab) { this.guard.closeTop(); return; }
    if (tab !== "towers" && !this.unlocked) { toast(this.parent, `Clear level ${UNLOCK_AFTER_LEVEL} to unlock ${TAB_NAMES[tab]}`); return; }
    if (this.features.openPage(tab)) return;                  // a feature's page replaces the tab's own
    if (tab === "towers") {
      // The page shows NEW on the towers not seen yet and points at the upgrade the tab's arrow was about; opening it is
      // the look, so the tab badge clears now.
      const fresh = this.newTowers(), nudged = upgradeNudge.shows(this.pick) ? UpgradeNudge.keyOf(this.pick) : null;
      this.towers.open(new Set(fresh), nudged);
      if (upgradeNudge.markSeen(this.pick) || fresh.length > 0) {
        if (fresh.length > 0) this.h.seen.markTowers([...(this.h.seen.towers() ?? []), ...fresh]);
        const slot = this.root.querySelector<HTMLElement>(".tab[data-tab=towers] .tbadge");
        if (slot) slot.innerHTML = this.towersBadgeHtml();
      }
    }
    else if (tab === "gear") this.h.onInventory();
    else if (tab === "shop") this.openShop("gold");
    else this.openEvents();
  }

  /**
   * The Events tab (M35): the Daily Orders and the check-in (the Chests tab's, until M35) over the rows the features
   * list there — the live event with its Trial and Heroic tiers, Endless, Free rewards — and the Iron challenges.
   */
  private openEvents(): void {
    const core: CoreMoreRow[] = [{
      order: 15, id: "iron", icon: "iron", label: "Iron challenges",
      note: this.features.ironOpen() ? "One long wave" : "Three-star a level",
      onClick: () => this.h.onCampaign(),
    }];
    const sheet = showMore(this.parent, this.features.moreRows(core, "events"), "", EVENTS_HEAD, eventsPageIntro(this.catalog));
    mountOrders(sheet, this.ordersCtx());
  }

  /** The ⚙ menu (M35, the More tab's rows that are not a currency's or an event's). */
  private openMenu(): void {
    guide()?.signal("tabOpened");
    showMore(this.parent, this.features.moreRows(this.coreMoreRows()), this.version);
  }

  /** The hub's own menu rows: the captain's Guide (when he is on), Settings, and Debug in `?debug` builds. */
  private coreMoreRows(): CoreMoreRow[] {
    const rows: CoreMoreRow[] = [];
    if (guide()) rows.push({ order: MORE_ORDER.guide, id: "guide", icon: "guide", label: "Guide", note: "Tips and a practice battle", onClick: () => this.guideLink.openSheet() });
    rows.push({ order: MORE_ORDER.settings, id: "settings", icon: "settings", label: "Settings", onClick: () => this.h.onSettings() });
    const debug = this.h.onDebug;
    if (debug) rows.push({ order: MORE_ORDER.debug, id: "debug", icon: "target", label: "Debug", onClick: () => debug() });
    return rows;
  }

  /** The builder chip: the sheet of what the builder works on, or should (a tower, the Fortress or a pet). */
  private openPick(): void {
    const t = pickTarget(this.pick);
    if (t?.target === UpgradeTarget.Pet) this.upgrade.openPet(this.B.pets.list[t.towerIndex]?.id ?? "");
    else if (t) this.upgrade.open(t.towerIndex);
  }

  private openCommander(): void {
    showCommander(this.parent, this.B, this.profile, this.nextLevel, this.readiness, (a) => this.onAction(a));
  }

  /** A "Get stronger" step: equip → Gear, upgrade → that tower's sheet, replay → the campaign map. */
  private onAction(a: ReadinessAction): void {
    if (a.kind === "equip") this.h.onInventory();
    else if (a.kind === "upgradeTower") { const i = this.B.towers.findIndex((t) => t.id === a.towerId); if (i >= 0) this.upgrade.open(i); }
    else this.h.onCampaign();
  }

  /** The sheet on top changed: light its tab and let the scene rest under a sheet that hides most of it. */
  private onSheet(sheet: HTMLElement | null): void {
    this.setTab(sheet ? tabForSheet([...sheet.classList]) : "battle");
    this.root.classList.toggle("sheetup", sheet !== null);   // BATTLE's plate drops into the bar (hub.css)
    if ((sheet ? sheetName([...sheet.classList]) : null) !== this.guideLink.sheet) this.guideLink.update();
    this.scene.paused(sheet !== null && sheet.getBoundingClientRect().height >= appHeight() * PAUSE_SHEET_FRACTION);
  }

  /** Lights `tab` in the tab bar (the sheet guard calls this whenever the sheet on top changes). */
  private setTab(tab: HubTab): void {
    this.tab = tab;
    for (const b of this.root.querySelectorAll<HTMLElement>(".tabbar .tab")) b.classList.toggle("on", b.dataset.tab === tab);
  }

  /** Upgrades that completed while the hub is open (a timer, a speed-up, VIP's free finish): the cue, the scene, ✓. */
  private jobsDone(done: readonly UpgradeJob[]): void {
    if (done.length === 0) return;
    audio.play("hubUpgradeDone");
    this.sceneBand.celebrate(done);
    this.doneUntil = this.now() + DONE_BADGE_SECONDS;
    this.h.onChange(); this.render(); this.upgrade.refresh(); this.towers.refresh();
  }

  private tick(): void {
    const done = this.profile.claimCompleted(this.now());
    // A job ran out (its timer, or a speed-up that finished it) while the hub is open: the building is done.
    if (done.length > 0) { this.jobsDone(done); return; }
    this.pick = builderPick(this.B, this.profile, this.catalog.ownedTowers(), this.now());
    const slot = this.root.querySelector<HTMLElement>(".tab[data-tab=towers] .tbadge");
    if (slot) slot.innerHTML = this.towersBadgeHtml();
    const ev = this.root.querySelector<HTMLElement>(".tab[data-tab=events] .ebadge");
    if (ev) ev.innerHTML = eventsBadgeHtml(this.catalog, this.features, this.now(), !this.unlocked);
    const shop = this.root.querySelector<HTMLElement>(".tab[data-tab=shop] .sbadge");
    if (shop) shop.innerHTML = shopBadgeHtml(this.features, this.researchDots(), !this.unlocked);
    const menu = this.root.querySelector<HTMLElement>(".topbar .menubtn .mdot");
    if (menu) menu.innerHTML = menuDotHtml(this.features);
    this.sceneBand.updateChip(this.pick);
    this.features.tickWidgets();
    this.upgrade.tick();
    this.towers.tick();
    this.guideLink.update();
    this.maybeShowCheckin();
    this.h.onTick?.();
  }

  private disposed = false;
  /** Stops the chips following the shot (HubSceneLink.follow). */
  private readonly unfollow: () => void;
  dispose(): void {
    this.disposed = true;
    this.unfollow();
    if (this.timer !== null) clearInterval(this.timer);
    this.guideLink.dispose();
    removeEventListener("resize", this.onResize);
    this.parent.classList.remove("hubtabs");
    this.scene.paused(false);
    this.upgrade.close();
    this.towers.dispose();
    this.guard.dispose();
    this.features.disposeWidgets();
    this.root.remove();
  }
}
